import { useEffect, useMemo, useState } from "react";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getAllRegistros } from "../../../services/registro.service";
import { getAllRegistrosV2 } from "../../../services/registroV2.service";
import { EstudianteActivoList } from "../../../modules/estudiante/EstudianteActivoList";
import { getAllGrupos } from "../../../services/grupo.service";

const normalizeText = (text = "") =>
  text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const isHabilitado = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  const v = String(value ?? "").trim().toUpperCase();
  if (["1", "TRUE", "T", "SI", "S", "HABILITADO"].includes(v)) return true;
  if (["0", "FALSE", "F", "NO", "N", "INHABILITADO"].includes(v)) return false;
  return true;
};

export const EstudiantesActivos = () => {
  const [estudiantes, setEstudiantes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [mes, setMes] = useState("");
  const [anio, setAnio] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargar = async () => {
      try {
        const [estudiantesResp, registros, registrosV2, gruposAll] = await Promise.all([
          getAllEstudiantes(),
          getAllRegistros(),
          getAllRegistrosV2().catch(() => []),
          getAllGrupos().catch(() => []),
        ]);

        const registrosPorEst = new Map();
        
        for (const reg of registros || []) {
          const idEst = reg?.idEstudiante?.trim?.();
          if (!idEst) continue;
          const anterior = registrosPorEst.get(idEst);
          if (!anterior) {
            registrosPorEst.set(idEst, reg);
          } else {
            const fa = anterior?.fechaRegistro ? new Date(anterior.fechaRegistro) : new Date(0);
            const fn = reg?.fechaRegistro ? new Date(reg.fechaRegistro) : new Date(0);
            if (fn >= fa) registrosPorEst.set(idEst, reg);
          }
        }

        for (const reg of registrosV2 || []) {
          const idEst = reg?.idEstudiante?.trim?.();
          if (!idEst) continue;
          
          const regNormalizado = {
            ...reg,
            idRegistro: reg.idRegistroNotasV2,
            estado: reg.estadoV2,
          };

          const anterior = registrosPorEst.get(idEst);
          if (!anterior) {
            registrosPorEst.set(idEst, regNormalizado);
          } else {
            const fa = anterior?.fechaRegistro ? new Date(anterior.fechaRegistro) : new Date(0);
            const fn = regNormalizado?.fechaRegistro ? new Date(regNormalizado.fechaRegistro) : new Date(0);
            const esAnteriorV1 = !anterior.idRegistroNotasV2;
            if (esAnteriorV1 || fn >= fa) {
              registrosPorEst.set(idEst, regNormalizado);
            }
          }
        }

        const gruposCache = new Map((gruposAll || []).map((g) => [g.idGrupo, g]));

        const enriquecidos = (estudiantesResp || []).map((est) => {
          const idEst = est?.estudiante?.idEstudiante?.trim?.();
          const reg = idEst ? registrosPorEst.get(idEst) : null;
          const estado = String(reg?.estado ?? "").trim();
          const habilitado = isHabilitado(
            est?.estudiante?.habilitado ?? est?.usuario?.habilitado ?? est?.persona?.habilitado
          );

          const tipoAdmisionNombre = est?.tipoAdmision?.nombre || est?.estudiante?.tipoAdmision || "N/A";

          let grupo = reg?.grupo;
          if (!grupo && reg?.idGrupo) {
            grupo = gruposCache.get(reg.idGrupo) || null;
          }

          return {
            ...est,
            estudiante: {
              ...est.estudiante,
              tipoAdmision: tipoAdmisionNombre,
              tipoCongelacion: reg?.tipoCongelacion ?? est?.estudiante?.tipoCongelacion,
            },
            registro: reg ? { ...reg, grupo } : null,
            _estadoRegistro: estado,
            _habilitadoBool: habilitado,
          };
        });

        const activos = enriquecidos.filter((e) => {
          const estado = e._estadoRegistro?.toLowerCase?.() || "";
          const esEstadoActivo = estado === "aprobado" || estado === "progreso";
          return esEstadoActivo && e._habilitadoBool === true;
        });

        setEstudiantes(activos);
      } catch (err) {
        console.error("Error cargando estudiantes activos:", err);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, []);

  const filtrados = useMemo(() => {
    const q = normalizeText(busqueda);
    return (estudiantes || []).filter(({ persona, usuario, estudiante: est, registro }) => {
      if (mes || anio) {
        const fechaRef = registro?.fechaRegistro ? new Date(registro.fechaRegistro) : null;
        if (!fechaRef) return false;
        const m = fechaRef.getMonth() + 1;
        const y = fechaRef.getFullYear();
        if (mes && m !== Number(mes)) return false;
        if (anio && y !== Number(anio)) return false;
      }
      const campos = [
        usuario?.username ?? "",
        persona?.nombre ?? "",
        persona?.apellido ?? "",
        persona?.celular ?? "",
        persona?.sexo ?? "",
        persona?.ci ?? "",
        est?.tipoAdmision ?? "",
        (est?.credito ?? "").toString(),
      ];
      const textOk = q ? normalizeText(campos.join(" ")).includes(q) : true;
      return textOk;
    });
  }, [busqueda, mes, anio, estudiantes]);

  return (
    <ContenedorPageTemplate titulo={"Estudiantes Activos"}>
      <div className="flex flex-col gap-3">
        <div className="flex gap-3 items-center">
          <select
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="border rounded-lg p-2"
          >
            <option value="">Todos los meses</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(0, i).toLocaleString("es", { month: "long" })}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={anio}
            onChange={(e) => setAnio(e.target.value)}
            placeholder="Todos los años"
            className="border rounded-lg p-2"
            min="2000"
            max={new Date().getFullYear() + 5}
          />
        </div>
        <div className="flex gap-4 py-3">
          <input
            type="text"
            placeholder="Buscar por nombre, apellido, código, Teléfono, tipo de admisión, CI, Género o crédito"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="flex-1 text-black border shadow-sm rounded-xl py-3 px-4 focus:ring-1 focus:border-blue-500 focus:ring-blue-300 transition-colors hover:border-blue-500 border-slate-200"
          />
        </div>
        <p>
          Total activos: <span>{filtrados?.length || 0}</span>
          {loading ? " (cargando...)" : ""}
        </p>
        <EstudianteActivoList estudiantes={filtrados} />
      </div>
    </ContenedorPageTemplate>
  );
};

export default EstudiantesActivos;


